import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api/client.js";
import { useI18n } from "../i18n/I18nContext.jsx";
import { useAuth } from "../context/AuthContext.jsx";
import OfficerForm from "../components/OfficerForm.jsx";
import StatusBadge from "../components/StatusBadge.jsx";
import "../styles/OfficerProfile.css";

function formatINR(n) {
  if (n === null || n === undefined || n === "") return "—";
  return "₹" + Number(n).toLocaleString("en-IN");
}

function fmtDateTime(s) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return s;
  }
}

export default function OfficerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user } = useAuth();
  
  const [officer, setOfficer] = useState(null);
  const [paras, setParas] = useState([]);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEdit, setShowEdit] = useState(false);

  const canManage = ["SUPER_ADMIN", "CORP_ADMIN", "ZONAL_AC"].includes(
    user?.role
  );

  const fetchOfficerAndParas = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get(`/auth/users/${id}/`),
      api.get(`/paras/?assigned_officer=${id}&page_size=100`),
      api.get(`/replies/?officer=${id}&page_size=100`),
    ])
      .then(([officerRes, parasRes, repliesRes]) => {
        setOfficer(officerRes.data);
        setParas(parasRes.data.results || parasRes.data);
        setReplies(repliesRes.data.results || repliesRes.data);
      })
      .catch((err) => {
        console.error(err);
        setError("Failed to fetch officer details.");
      })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    fetchOfficerAndParas();
  }, [fetchOfficerAndParas]);

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this officer? This action cannot be undone.")) return;
    try {
      await api.delete(`/auth/users/${id}/`);
      navigate("/officers");
    } catch (err) {
      alert("Failed to delete officer. They may be assigned to active paras.");
    }
  };

  if (loading) return <div className="center-screen">{t("loading")}</div>;
  if (error) return <div className="center-screen error-text">{error}</div>;
  if (!officer) return <div className="center-screen">Officer not found.</div>;

  const totalParas = paras.length;
  const resolvedParas = paras.filter(p => p.status === "RESOLVED" || p.status === "CLOSED").length;
  const pendingParas = totalParas - resolvedParas;
  const amountPending = paras
    .filter(p => p.status !== "RESOLVED" && p.status !== "CLOSED")
    .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

  return (
    <div className="page-officer-profile">
      <div className="page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <button className="btn btn-outline" onClick={() => navigate(-1)} style={{ marginBottom: "1rem" }}>
            &larr; Back
          </button>
          <h1>Officer Profile - {officer.full_name}</h1>
        </div>
        {canManage && (
          <div style={{ display: "flex", gap: "0.5rem", marginTop: "2.5rem" }}>
            <button className="btn btn-primary" onClick={() => setShowEdit(true)}>
              Edit Officer
            </button>
            <button className="btn btn-outline" style={{ borderColor: "red", color: "red" }} onClick={handleDelete}>
              Delete Officer
            </button>
          </div>
        )}
      </div>

      <div className="stat-grid" style={{ marginBottom: "2rem" }}>
        <div className="stat-card" onClick={() => navigate(`/paras?assigned_officer=${id}`)} style={{ cursor: "pointer" }}>
          <div className="stat-value">{totalParas}</div>
          <div className="stat-label">{t("totalParas")}</div>
        </div>
        <div className="stat-card red">
          <div className="stat-value">{pendingParas}</div>
          <div className="stat-label">{t("pendingParas")}</div>
        </div>
        <div className="stat-card green">
          <div className="stat-value">{resolvedParas}</div>
          <div className="stat-label">{t("resolvedParas")}</div>
        </div>

        <div className="stat-card red">
          <div className="stat-value" style={{ fontSize: 20 }}>
            {formatINR(amountPending)}
          </div>
          <div className="stat-label">{t("amountPending")}</div>
        </div>
      </div>

      <div className="card">
        <h3>Officer Details</h3>
        <div className="detail-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
          <div className="detail-item">
            <span className="muted">{t("fullName")}: </span>
            <strong>{officer.full_name}</strong>
          </div>
          <div className="detail-item">
            <span className="muted">{t("mobile")}: </span>
            <span>{officer.mobile}</span>
          </div>
          <div className="detail-item">
            <span className="muted">{t("role")}: </span>
            <span className="tag-pill">{officer.role_display}</span>
          </div>
          <div className="detail-item">
            <span className="muted">{t("designation")}: </span>
            <span>{officer.designation || "—"}</span>
          </div>
          <div className="detail-item">
            <span className="muted">{t("department")}: </span>
            <span>{officer.department || "—"}</span>
          </div>
          <div className="detail-item">
            <span className="muted">{t("zone")}: </span>
            <span>{officer.zone_name || "—"}</span>
          </div>
        </div>
      </div>

      <h3 style={{ marginTop: "2rem", marginBottom: "1rem", color: "var(--navy)" }}>Assigned Paras</h3>
      {paras.length === 0 ? (
        <div className="card" style={{ textAlign: "center", color: "var(--muted)", padding: "2rem" }}>
          No paras assigned to this officer.
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Para No</th>
                  <th>Department</th>
                  <th>Status</th>
                  <th>Amount</th>
                  <th>Deadline</th>
                </tr>
              </thead>
              <tbody>
                {paras.map((p) => (
                  <tr 
                    key={p.id} 
                    onClick={() => navigate(`/paras/${p.id}`)}
                    style={{ cursor: "pointer" }}
                    className="hoverable-row"
                  >
                    <td>
                      <strong>{p.para_no}</strong>
                    </td>
                    <td>{p.department || "—"}</td>
                    <td>
                      <StatusBadge status={p.status} />
                    </td>
                    <td>{p.amount ? formatINR(p.amount) : "—"}</td>
                    <td>{p.deadline || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <h3 style={{ marginTop: "2rem", marginBottom: "1rem", color: "var(--navy)" }}>
        Submitted Replies
      </h3>
      {replies.length === 0 ? (
        <div className="card" style={{ textAlign: "center", color: "var(--muted)", padding: "2rem" }}>
          No replies submitted by this officer yet.
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Para No</th>
                  <th>Submitted</th>
                  <th>Explanation</th>
                  <th>Documents</th>
                  <th>Review</th>
                </tr>
              </thead>
              <tbody>
                {replies.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => navigate(`/paras/${r.para}`)}
                    style={{ cursor: "pointer" }}
                    className="hoverable-row"
                  >
                    <td>
                      <strong>{r.para_no || "—"}</strong>
                      {r.para_audit_year && (
                        <div className="muted" style={{ fontSize: "11px" }}>
                          {r.para_audit_year}
                        </div>
                      )}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>{fmtDateTime(r.submitted_at)}</td>
                    <td style={{ maxWidth: 320, whiteSpace: "normal" }}>
                      {r.explanation
                        ? r.explanation.length > 140
                          ? r.explanation.slice(0, 140) + "…"
                          : r.explanation
                        : "—"}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {r.evidence && r.evidence.length > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          {r.evidence.map((ev) => {
                            const name = ev.file ? ev.file.split("/").pop() : "Document";
                            return (
                              <a
                                key={ev.id}
                                href={ev.file}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={ev.description || name}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 4,
                                  color: "var(--accent)",
                                  fontSize: "12px",
                                  textDecoration: "none",
                                  fontWeight: 600,
                                  maxWidth: 180,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                <i className="ti ti-paperclip" style={{ fontSize: 13 }}></i>
                                {name}
                              </a>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td>
                      <StatusBadge status={r.review_status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showEdit && (
        <OfficerForm
          officer={officer}
          onClose={() => setShowEdit(false)}
          onDone={() => {
            setShowEdit(false);
            fetchOfficerAndParas();
          }}
        />
      )}
    </div>
  );
}
